import {Box, Grid} from '@material-ui/core';
import classNames from 'classnames';
import IconWithTooltip from '../../Tooltip/IconWithTooltip';
import TextField from '../../TextField/TextField';
import PasswordTextField from '../../PasswordTextField/PasswordTextField';
import {getConfigFromPasswordRulesOrDefault} from '../../../utils/passwordGenerator';
import SelectField from '../../SelectField/SelectField';
import {PermissionType} from '../../../store/types/Permission';
import ExtensionAsyncSelectFiled from '../../CallControls/ExtensionAsyncSelectFiled';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {CreateNewPortalUserFormData, useStyles} from '../utils';
import {ConfigData} from '../../../store/types/ConfigData';
import {FormikErrors} from 'formik';
import {CustomerOfficeType} from '../../../store/types/CustomerInfo';
import {RoleUserItem} from '../../../store/actions/company/payloads';
import {useSelector} from 'react-redux';
import {ReduxState} from '../../../store/types';
import {usePermissionContext} from '../../../hooks/usePermissions';

interface GeneralSettingsSectionProps {
    isSupervisor?: boolean;
    values: CreateNewPortalUserFormData;
    initialValues: CreateNewPortalUserFormData;
    handleChange: (e: React.ChangeEvent<any>) => void;
    hidePassword: boolean;
    config?: ConfigData;
    errors: FormikErrors<CreateNewPortalUserFormData>;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => void;
    setFieldError: (field: string, message: string | undefined) => void;
    callback2?: (flag: boolean) => void;
    roleList?: RoleUserItem[];
}

const GeneralSettingsSection: React.VFC<GeneralSettingsSectionProps> = ({
    isSupervisor,
    initialValues,
    values,
    handleChange,
    hidePassword,
    config,
    errors,
    setFieldValue,
    setFieldError,
    callback2,
    roleList,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const permissions = usePermissionContext();

    const i_office_type = useSelector<
        ReduxState,
        CustomerOfficeType | undefined
    >((state) => state.generic?.customerOfficeType);

    const formatedRoleList = useMemo(() => {
        return (
            roleList
                ?.filter((e) => e.name !== t('common:any', 'Any'))
                ?.filter((e) =>
                    i_office_type === CustomerOfficeType.Branch
                        ? e.i_role_type !== 7
                        : true,
                )
                ?.map((v) => v.name) || []
        );
    }, [roleList,i_office_type]);

    const formatedIcon = useMemo(() => {
        return roleList?.find((e) => e.name === values.role)
            ?.description ? (
            <IconWithTooltip
                dataQa="my-company-address"
                tooltipText={
                    roleList?.find(
                        (e) => e.name === values.role,
                    )?.description || ''
                }
            />
        ) : undefined
    }, [roleList])


    const typeToShow = isSupervisor && values.type === 'customer' ? t(
        'screens:addToRingGroup.administrator',
    ).toString() : t(
        'screens:addToRingGroup.supervisor',
    ).toString()

    return (
        <Grid
            item
            className={classNames(classes.itemsContainer, classes.marginBottom)}
            data-testid={'edit-user-settings'}
        >
            <Box className={classes.headerBox} width={350}>
                <span className={classes.rowBoxHeader}>
                    {t('screens:myCompany.general')}
                </span>

                <IconWithTooltip
                    dataQa="my-company-address"
                    tooltipText={t('tooltips:myCompany.general')}
                />
            </Box>
            <Box className={classes.rowBox}>
                <TextField
                    id="login"
                    label={t('common:login')}
                    onChange={handleChange}
                    value={values.login}
                    setFieldError={setFieldError}
                    dataQa="create-portal-user-login"
                    helperText={errors?.login}
                    required
                    maxLength={64}
                />

                {!hidePassword && (
                    <PasswordTextField
                        id="password"
                        label={t('common:password')}
                        value={values.password}
                        onChange={handleChange}
                        dataQa="create-extension-name-input"
                        setFieldError={setFieldError}
                        error={errors.password}
                        isRequired={true}
                        passwordRulesConfig={getConfigFromPasswordRulesOrDefault(
                            // @ts-ignore
                            config,
                            true,
                        )}
                        setFieldValue={setFieldValue}
                        maxLength={32}
                        inputProps={{
                            inputProps: {
                                min: config?.Web.PasswordMinLength || 0,
                                maxLength: 32,
                            },
                        }}
                        passwordProtectionMode
                        initialValue={initialValues.password}
                    />
                )}
            </Box>
            <Box className={classes.rowBox}>
                <TextField
                    id="email"
                    label={t('common:emailAddress')}
                    onChange={handleChange}
                    value={values.email}
                    setFieldError={setFieldError}
                    dataQa="create-portal-user-email"
                    helperText={errors?.email}
                    maxLength={128}
                />

                {!isSupervisor && (
                    <SelectField
                        id="role"
                        onChange={(e, v) => {
                            setFieldValue('role', v);
                            if (callback2) {
                                const role = roleList?.find((r) => r.name == v);
                                if (role?.i_role_type == 7) {
                                    callback2(true);
                                } else {
                                    callback2(false);
                                }
                            }
                        }}
                        label={t('screens:portalUsers.role')}
                        items={formatedRoleList}
                        value={values.role}
                        dataQa="create-portal-user-role"
                        readOnly={
                            values?.isCurrentLoginUser ||
                            permissions == PermissionType.ReadOnly
                        }
                        className={classNames(
                            values?.isCurrentLoginUser ||
                                permissions == PermissionType.ReadOnly
                                ? classes.readOnlyRole
                                : null,
                        )}
                        classes={{
                            iconContainer: classNames(
                                values?.isCurrentLoginUser ||
                                    permissions == PermissionType.ReadOnly
                                    ? classes.readOnlyRoleIconContainer
                                    : null,
                            ),
                        }}
                        icon={formatedIcon}
                        helperText={errors?.role}
                        required
                        setFieldError={setFieldError}
                    />
                )}
            </Box>

            {roleList?.find((role) => role.name == values.role)?.i_role_type ==
                7 && (
                <Box className={classes.rowBox}>
                    <ExtensionAsyncSelectFiled
                        id={'extension'}
                        // @ts-ignore
                        value={values.extension}
                        setValue={(value) => {
                            setFieldValue('extension', value);
                        }}
                        customClass={classes.extensionField}
                        required={true}
                        useAny={false}
                        showRegisterStatus={false}
                        disableClearable={true}
                        forceEnable={true}
                        useExtensionFilter={true}
                        setFieldError={setFieldError}
                        helperText={errors.extension}
                    />
                </Box>
            )}

            {isSupervisor  && (
                <Box className={classes.rowBox}>
                    <TextField
                        label={t('common:type')}
                        value={typeToShow}
                        readOnly
                        iconPosition={'end'}
                        icon={
                            <IconWithTooltip
                                dataQa="profile-roles"
                                tooltipText={t('tooltips:profiles.roles')}
                            />
                        }
                    />
                </Box>
            )}
        </Grid>
    );
};

export default GeneralSettingsSection;
